import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, Trash, PaperPlaneTilt, Copy, Sparkle, ChartBar, ClipboardText, UploadSimple, Ticket, DownloadSimple, WhatsappLogo, CheckCircle, XCircle, Clock, MagicWand } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { API, apiErr } from "../../lib/api";
import PublicEvents from "./PublicEvents";

const RESP_LABEL = { attending: "Attending", maybe: "Maybe", not_sure: "Not sure", not_attending: "Not attending", confirmed: "Confirmed", declined: "Declined" };
const GATED = ["rsvp", "invite_only", "approval"];
const modeOf = (e) => (e.occurrences || [{}])[0]?.mode || "public";
const badgeFor = (st) => (["attending", "confirmed"].includes(st) ? "confirmed" : ["not_attending", "declined"].includes(st) ? "rejected" : "pending");

export default function Guests() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("list");
  const [scope, setScope] = useState("private");
  const [guests, setGuests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [template, setTemplate] = useState({ subject: "", message: "" });
  const [approvals, setApprovals] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [single, setSingle] = useState({ name: "", email: "", mobile: "" });
  const [bulk, setBulk] = useState("");
  const [smart, setSmart] = useState("");
  const [parsed, setParsed] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [dupes, setDupes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get("/organizer/events").then((r) => {
      const gated = (r.data.events || []).filter((e) => GATED.includes(modeOf(e)));
      setEvents(gated);
      setEventId((gated[0] || {}).id || "");
    }).finally(() => setLoading(false));
  }, []);

  const curEvent = events.find((e) => e.id === eventId);
  const mode = curEvent ? modeOf(curEvent) : "rsvp";

  useEffect(() => { if (mode === "approval") setTab("requests"); else if (tab === "requests") setTab("list"); }, [eventId]); // eslint-disable-line

  const loadAll = () => {
    if (!eventId) return;
    if (mode === "approval") {
      api.get(`/organizer/events/${eventId}/approvals`).then((r) => setApprovals(r.data.approvals || []));
    } else {
      api.get(`/organizer/events/${eventId}/guests`).then((r) => setGuests(r.data.guests || []));
      api.get(`/organizer/events/${eventId}`).then((r) => {
        setQuestions(r.data.rsvp_questions || []);
        setTemplate(r.data.invitation_template || { subject: "", message: "" });
      });
    }
    api.get(`/organizer/events/${eventId}/rsvp-analytics`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    setAiSummary(null); setDupes(null);
  };
  useEffect(() => { loadAll(); }, [eventId]); // eslint-disable-line

  const rsvpLink = (t) => `${window.location.origin}/rsvp/${t}`;
  const attending = useMemo(() => guests.filter((g) => ["attending", "confirmed"].includes((g.response || {}).status)), [guests]);
  const headcount = useMemo(() => attending.reduce((s, g) => s + (parseInt((g.response?.custom_answers || {}).party_size, 10) || 1), 0), [attending]);

  // ---- guest CRUD ----
  const addSingle = async () => {
    if (!single.name.trim()) return toast.error("Enter guest name.");
    setBusy(true);
    try {
      const { data } = await api.post(`/organizer/events/${eventId}/guests`, { guests: [single] });
      toast[data.added_count ? "success" : "info"](data.added_count ? "Guest added." : "Duplicate — already exists.");
      setSingle({ name: "", email: "", mobile: "" }); setAddOpen(false); loadAll();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const importBulk = async () => {
    const rows = bulk.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [name, contact = ""] = l.split(/[,\t]/).map((p) => p.trim());
      return contact.includes("@") ? { name, email: contact } : { name, mobile: contact };
    }).filter((g) => g.name);
    if (!rows.length) return toast.error("Add at least one line: Name, email/mobile.");
    await saveGuests(rows);
  };
  const smartImport = async () => {
    if (!smart.trim()) return;
    setAiBusy(true);
    try { const { data } = await api.post(`/organizer/events/${eventId}/guests/ai-parse`, { text: smart }); setParsed(data.guests || []); toast.success(`AI found ${data.guests.length} guest(s) — review & add.`); }
    catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };
  const saveGuests = async (rows) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/organizer/events/${eventId}/guests`, { guests: rows });
      toast.success(`${data.added_count} added${data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate(s) skipped` : ""}.`);
      setBulk(""); setSmart(""); setParsed(null); setAddOpen(false); loadAll();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const removeGuest = async (gid) => { try { await api.delete(`/organizer/guests/${gid}`); toast.success("Guest removed."); loadAll(); } catch (e) { toast.error(apiErr(e)); } };

  // ---- invitations & tickets ----
  const sendAll = async () => { setBusy(true); try { const { data } = await api.post(`/organizer/events/${eventId}/guests/send`, {}); toast.success(data.message); loadAll(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const sendOne = async (gid) => { try { const { data } = await api.post(`/organizer/events/${eventId}/guests/send`, { guest_ids: [gid] }); toast.success(data.message); loadAll(); } catch (e) { toast.error(apiErr(e)); } };
  const resendTicket = async (gid) => { try { const { data } = await api.post(`/organizer/guests/${gid}/resend-ticket`); toast.success(data.message); loadAll(); } catch (e) { toast.error(apiErr(e)); } };
  const downloadTicket = (g) => window.open(`${API}/rsvp/${g.token}/ticket.pdf`, "_blank");
  const shareTicketWa = (g) => window.open(`https://wa.me/?text=${encodeURIComponent(`Your pass for ${curEvent.title}. View & download: ${rsvpLink(g.token)}`)}`, "_blank");

  // ---- AI ----
  const runSummary = async () => { setAiBusy(true); try { const { data } = await api.post(`/organizer/events/${eventId}/ai/attendee-summary`); setAiSummary(data); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const runDupes = async () => { setAiBusy(true); try { const { data } = await api.post(`/organizer/events/${eventId}/ai/find-duplicates`); setDupes(data); toast.success(data.note || "Done."); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const aiQuestions = async () => { setAiBusy(true); try { const { data } = await api.post(`/organizer/events/${eventId}/ai/rsvp-questions`); setQuestions(data.questions || []); toast.success("AI suggested questions."); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const aiInvitation = async () => { setAiBusy(true); try { const { data } = await api.post(`/organizer/events/${eventId}/ai/invitation`, { tone: "warm" }); setTemplate({ subject: data.subject, message: data.message }); toast.success("AI drafted an invitation."); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const saveConfig = async () => { setBusy(true); try { await api.put(`/organizer/events/${eventId}/rsvp-config`, { rsvp_questions: questions, invitation_template: template }); toast.success("RSVP settings saved."); loadAll(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const setQ = (i, patch) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const addQ = () => setQuestions((qs) => [...qs, { id: `q_${Date.now()}`, label: "", type: "text", options: [], required: false }]);

  // ---- approvals ----
  const decide = async (rid, action) => { try { const { data } = await api.post(`/organizer/approvals/${rid}/${action}`); toast.success(data.message); loadAll(); } catch (e) { toast.error(apiErr(e)); } };

  if (loading) return <PageLoader />;

  const TABS = mode === "approval"
    ? [["requests", "Requests", ClipboardText], ["analytics", "Analytics", ChartBar]]
    : [["list", "Invitees", Users], ["form", "Invitation & form", ClipboardText], ["analytics", "Analytics", ChartBar]];

  return (
    <div className={scope === "public" ? "max-w-6xl" : "max-w-4xl"}>
      <div className="overline mb-1">Guests & RSVP</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4">Guests & RSVP</h1>

      <div className="flex gap-2 mb-6 p-1 rounded-full bg-muted w-fit" data-testid="guests-scope-toggle">
        {[["private", "Private (Guests & RSVP)"], ["public", "Public Events"]].map(([v, l]) => (
          <button key={v} onClick={() => setScope(v)} data-testid={`guests-scope-${v}`}
            className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${scope === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {scope === "public" ? <PublicEvents /> : events.length === 0 ? (
        <EmptyState icon={Users} title="No private events yet" subtitle="Create an RSVP, Invite-only or Approval event to manage guests."
          action={<Button asChild className="rounded-full"><Link to="/organizer/events/new">Create event</Link></Button>} testid="no-events-guests" />
      ) : (
        <>
          <div className="w-full sm:w-80 mb-5">
            <Label className="mb-1.5 block">Event</Label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger data-testid="guests-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title} · {modeOf(e).replace("_", "-")}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">Mode: <span className="font-semibold capitalize">{mode.replace("_", " ")}</span>
              {mode === "rsvp" && " — attendees get a free QR pass automatically."}
              {mode === "invite_only" && " — invitees pay to confirm, then get their QR ticket."}
              {mode === "approval" && " — approve requests, then the guest pays to get a ticket."}
            </p>
          </div>

          <div className="flex gap-2 mb-6 p-1 rounded-full bg-muted w-fit flex-wrap">
            {TABS.map(([v, l, Icon]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`guests-tab-${v}`}
                className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>
                <Icon size={15} /> {l}
              </button>
            ))}
          </div>

          {/* ---------------- Guest list (rsvp / invite) ---------------- */}
          {tab === "list" && mode !== "approval" && (
            <div>
              {/* AI toolbar */}
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 mb-4 flex flex-wrap gap-2 items-center">
                <span className="text-sm font-semibold text-primary inline-flex items-center gap-1.5"><Sparkle weight="fill" size={15} /> AI assist</span>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={runSummary} disabled={aiBusy} data-testid="ai-summary-btn"><MagicWand size={13} className="mr-1" /> Invitee summary</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={runDupes} disabled={aiBusy} data-testid="ai-dupes-btn"><Users size={13} className="mr-1" /> Find duplicates</Button>
              </div>

              {aiSummary && (
                <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="ai-summary-panel">
                  <div className="flex items-center justify-between"><h3 className="font-heading font-semibold">AI attendee summary</h3><span className="text-sm font-bold text-primary">{aiSummary.headcount} expected</span></div>
                  <p className="text-sm text-muted-foreground mt-1">{aiSummary.summary}</p>
                  {aiSummary.highlights?.length > 0 && <ul className="mt-2 text-sm list-disc pl-5 space-y-0.5">{aiSummary.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>}
                  {aiSummary.follow_ups?.length > 0 && <div className="mt-2 text-xs text-muted-foreground"><span className="font-semibold">Follow-ups:</span> {aiSummary.follow_ups.join(" · ")}</div>}
                </div>
              )}
              {dupes && dupes.duplicates?.length > 0 && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-4" data-testid="ai-dupes-panel">
                  <h3 className="font-heading font-semibold text-amber-200">Possible duplicates</h3>
                  {dupes.duplicates.map((d, i) => <div key={i} className="text-sm mt-1">{(d.names || d.ids).join(" ↔ ")} <span className="text-muted-foreground">— {d.reason}</span></div>)}
                </div>
              )}

              {/* headcount summary */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Metric label="Invited" value={guests.length} />
                <Metric label={mode === "invite_only" ? "Confirmed" : "Attending"} value={attending.length} />
                <Metric label="Expected headcount" value={headcount} accent />
              </div>

              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-muted-foreground">Each guest has a unique response link.</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-full" onClick={sendAll} disabled={busy || !guests.length} data-testid="send-all-btn"><PaperPlaneTilt className="mr-1.5" /> Send all</Button>
                  <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-guest-btn"><Plus className="mr-1.5" /> Add guests</Button>
                </div>
              </div>

              {guests.length === 0 ? (
                <EmptyState icon={Users} title="No guests yet" subtitle="Add guests, paste a list, or let AI import them." testid="no-guests" />
              ) : (
                <div className="space-y-2">
                  {guests.map((g) => {
                    const st = (g.response || {}).status;
                    const isAttending = ["attending", "confirmed"].includes(st);
                    return (
                      <div key={g.id} className="flex items-center justify-between gap-2 p-3 rounded-xl border border-border bg-card" data-testid={`guest-${g.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{g.name} {g.ticket_issued && <Ticket size={13} className="inline text-emerald-400" title="Pass issued" />}</div>
                          <div className="text-xs text-muted-foreground truncate">{g.email || g.mobile || "no contact"}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {st ? <StatusBadge status={badgeFor(st)} /> : <span className="text-xs text-muted-foreground capitalize">{g.invite_status}</span>}
                          {mode === "rsvp" && isAttending ? (
                            <>
                              <button onClick={() => resendTicket(g.id)} className="text-primary" title="Send / resend pass" data-testid={`resend-ticket-${g.id}`}><PaperPlaneTilt size={15} /></button>
                              <button onClick={() => downloadTicket(g)} className="text-navy" title="Download / print pass" data-testid={`download-ticket-${g.id}`}><DownloadSimple size={15} /></button>
                              <button onClick={() => shareTicketWa(g)} className="text-emerald-400" title="Share on WhatsApp" data-testid={`share-ticket-${g.id}`}><WhatsappLogo size={15} /></button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => { navigator.clipboard.writeText(rsvpLink(g.token)); toast.success("Link copied."); }} className="text-primary" title="Copy link" data-testid={`copy-rsvp-${g.id}`}><Copy size={15} /></button>
                              <button onClick={() => sendOne(g.id)} className="text-muted-foreground hover:text-foreground" title="Send invite" data-testid={`send-guest-${g.id}`}><PaperPlaneTilt size={15} /></button>
                            </>
                          )}
                          <button onClick={() => removeGuest(g.id)} className="text-destructive" title="Remove" data-testid={`del-guest-${g.id}`}><Trash size={15} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {mode === "invite_only" && attending.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3">Invite-only is paid — confirmed guests get their QR ticket automatically once they complete payment via their invite link.</p>
              )}
            </div>
          )}

          {/* ---------------- Requests (approval) ---------------- */}
          {tab === "requests" && mode === "approval" && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Metric label="Pending" value={approvals.filter((a) => a.status === "pending").length} accent />
                <Metric label="Approved" value={approvals.filter((a) => a.status === "approved").length} />
                <Metric label="Paid / ticketed" value={approvals.filter((a) => a.status === "paid").length} />
              </div>
              {approvals.length === 0 ? (
                <EmptyState icon={ClipboardText} title="No requests yet" subtitle="Attendance requests will appear here for approval." testid="no-approvals" />
              ) : (
                <div className="space-y-2">
                  {approvals.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 p-3 rounded-xl border border-border bg-card" data-testid={`approval-${a.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{a.user_name} <span className="text-muted-foreground">· {a.qty} ticket(s)</span></div>
                        <div className="text-xs text-muted-foreground truncate">{a.user_email} · {a.occurrence_label}</div>
                        {a.note && <div className="text-xs text-muted-foreground mt-0.5 italic">"{a.note}"</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.status === "pending" && (
                          <>
                            <Button size="sm" className="rounded-full h-8" onClick={() => decide(a.id, "approve")} data-testid={`approve-${a.id}`}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                            <Button size="sm" variant="outline" className="rounded-full h-8 text-destructive border-destructive/30" onClick={() => decide(a.id, "reject")} data-testid={`reject-${a.id}`}><XCircle size={14} className="mr-1" /> Reject</Button>
                          </>
                        )}
                        {a.status === "approved" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400"><Clock size={13} /> Awaiting payment</span>}
                        {a.status === "paid" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400"><Ticket size={13} /> Paid · ticket issued</span>}
                        {a.status === "rejected" && <StatusBadge status="rejected" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---------------- Invitation & form ---------------- */}
          {tab === "form" && mode !== "approval" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-lg font-semibold">Invitation message</h2>
                  <Button size="sm" variant="outline" className="rounded-full border-primary/30 text-primary" onClick={aiInvitation} disabled={aiBusy} data-testid="ai-invitation-btn"><Sparkle weight="fill" className="mr-1.5" size={14} /> AI write</Button>
                </div>
                <div className="space-y-3">
                  <div><Label>Subject</Label><Input value={template.subject || ""} onChange={(e) => setTemplate({ ...template, subject: e.target.value })} className="mt-1.5" data-testid="invite-subject" /></div>
                  <div><Label>Message</Label><Textarea value={template.message || ""} onChange={(e) => setTemplate({ ...template, message: e.target.value })} rows={3} className="mt-1.5" placeholder="Use {name} for the guest's name" data-testid="invite-message" /></div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-lg font-semibold">RSVP questions</h2>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full border-primary/30 text-primary" onClick={aiQuestions} disabled={aiBusy} data-testid="ai-questions-btn"><Sparkle weight="fill" className="mr-1.5" size={14} /> AI suggest</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={addQ} data-testid="add-question-btn"><Plus size={14} className="mr-1" /> Add</Button>
                  </div>
                </div>
                {questions.length === 0 ? <p className="text-sm text-muted-foreground">No questions yet.</p> : (
                  <div className="space-y-3">
                    {questions.map((q, i) => (
                      <div key={q.id || i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`question-${i}`}>
                        <div className="flex gap-2">
                          <Input value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} placeholder="Question" data-testid={`q-label-${i}`} />
                          <Select value={q.type} onValueChange={(v) => setQ(i, { type: v })}>
                            <SelectTrigger className="w-32 shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="text">Text</SelectItem><SelectItem value="number">Number</SelectItem><SelectItem value="select">Choice</SelectItem><SelectItem value="boolean">Yes/No</SelectItem></SelectContent>
                          </Select>
                          <button onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))} className="text-destructive px-1"><Trash size={16} /></button>
                        </div>
                        {q.type === "select" && <Input value={(q.options || []).join(", ")} onChange={(e) => setQ(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Options, comma separated" />}
                        <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={!!q.required} onChange={(e) => setQ(i, { required: e.target.checked })} /> Required</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={saveConfig} disabled={busy} className="rounded-full" data-testid="save-rsvp-config">Save settings</Button>
            </div>
          )}

          {/* ---------------- Analytics ---------------- */}
          {tab === "analytics" && (
            analytics ? (
              <div className="space-y-5" data-testid="rsvp-analytics">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Metric label="Invited" value={analytics.invited} />
                  <Metric label="Responded" value={analytics.responded} />
                  <Metric label="No response" value={analytics.no_response} />
                  <Metric label="Expected attendance" value={analytics.expected_attendance} accent />
                </div>
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="font-heading font-semibold mb-3">Responses</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(analytics.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="px-3 py-1.5 rounded-full bg-muted text-sm font-medium">{RESP_LABEL[k] || k}: {v}</span>
                    ))}
                    {Object.values(analytics.by_status || {}).every((v) => v === 0) && <p className="text-sm text-muted-foreground">No responses yet.</p>}
                  </div>
                </div>
                {Object.keys(analytics.custom_answers || {}).length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-5">
                    <h3 className="font-heading font-semibold mb-3">Answer breakdown</h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.custom_answers).map(([k, dist]) => (
                        <div key={k}>
                          <div className="text-sm font-medium capitalize mb-1">{k.replace(/_/g, " ")}</div>
                          <div className="flex flex-wrap gap-2">{Object.entries(dist).map(([val, cnt]) => <span key={val} className="px-2.5 py-1 rounded-full bg-muted text-xs">{val}: {cnt}</span>)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground">No analytics available.</p>
          )}
        </>
      )}

      {/* add guests dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-guest-dialog">
          <DialogHeader><DialogTitle>Add guests</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="text-sm font-semibold text-primary inline-flex items-center gap-1.5"><Sparkle weight="fill" size={14} /> Smart import (AI)</div>
              <p className="text-xs text-muted-foreground">Paste anything — messy names, emails, phone numbers. AI will clean it up.</p>
              <Textarea value={smart} onChange={(e) => setSmart(e.target.value)} rows={3} placeholder={"e.g. aisha khan aisha@x.com, RAHUL +91 98200 00002, Priya priya@y.io"} data-testid="smart-text" />
              <Button onClick={smartImport} disabled={aiBusy} variant="outline" className="w-full rounded-full border-primary/30 text-primary" data-testid="smart-parse"><Sparkle className="mr-1.5" /> {aiBusy ? "Reading…" : "Parse with AI"}</Button>
              {parsed && (
                <div className="rounded-lg border border-border bg-background p-2 space-y-1 max-h-40 overflow-y-auto">
                  {parsed.map((p, i) => <div key={i} className="text-sm flex justify-between"><span>{p.name}</span><span className="text-muted-foreground text-xs">{p.email || p.mobile || "—"}</span></div>)}
                  <Button onClick={() => saveGuests(parsed)} disabled={busy} className="w-full rounded-full mt-1" data-testid="smart-add"><Plus className="mr-1.5" /> Add {parsed.length} guest(s)</Button>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="text-sm font-semibold">Single guest</div>
              <Input value={single.name} onChange={(e) => setSingle({ ...single, name: e.target.value })} placeholder="Name" data-testid="guest-name" />
              <Input value={single.email} onChange={(e) => setSingle({ ...single, email: e.target.value })} placeholder="Email (optional)" data-testid="guest-email" />
              <Input value={single.mobile} onChange={(e) => setSingle({ ...single, mobile: e.target.value })} placeholder="Mobile (optional)" data-testid="guest-mobile" />
              <Button onClick={addSingle} disabled={busy} className="w-full rounded-full" data-testid="submit-guest"><Plus className="mr-1.5" /> Add guest</Button>
            </div>
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="text-sm font-semibold">Bulk paste</div>
              <p className="text-xs text-muted-foreground">One per line: <span className="font-mono">Name, email or mobile</span>.</p>
              <Textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={3} placeholder={"Aisha, aisha@example.com\nRahul, +919800000002"} data-testid="bulk-guests" />
              <Button onClick={importBulk} disabled={busy} variant="outline" className="w-full rounded-full" data-testid="submit-bulk"><UploadSimple className="mr-1.5" /> Import list</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className={`p-4 rounded-xl border ${accent ? "brand-gradient text-white border-transparent shadow-lg shadow-primary/20" : "bg-card border-border"}`}>
      <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
      <div className="font-heading text-2xl font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
